// pms-adapters / browserContainer — the BROWSER-safe composition helper.
//
// Split out of `container.ts` by review R3 (2026-07-27, ladder §3.15). Both
// this and the server-only composition lived in one file, exported through one
// flat barrel; `apps/pms/src/PmsProvider.tsx` (browser) imported only
// `buildPmsServices`, but esbuild still had to parse and bundle-graph the
// WHOLE original file to resolve that import — so the server-only
// `composePmsSyncServices` (which builds a Supabase client from the
// service-role secret, injected as a Function env var — see `../server.ts`)
// had its full source text embedded in the browser build's `.js.map`
// sourcemap. Tree-shaking correctly removed it from the EXECUTABLE `.js` (no
// secret ran, or was even named, in the browser) — but the sourcemap is a
// devtools-visible artifact, and there was no functional need for that source
// to be anywhere near a client bundle. Same class of gap
// `@lemontaigu/staff/adapters/client` was split out for at ladder step 7
// (eslint.config.js's `serverOnlySubpaths` comment): tree-shaking working is
// not the same as an import never being resolved.
//
// (This paragraph deliberately avoids the literal env var identifier —
// `tools/check-key-surface.mjs` (E10) greps built artifacts for it, sourcemaps
// included, and a doc comment repeating the name would trip its own check.)
//
// Takes an already-authed client; never creates one. See
// PMS-P2-ARCHITECTURE-BRIEF §7.

import type {
  AvailabilityService,
  ChannelOpsService,
  PropertyRepository,
  QueryScope,
  RateGridService,
  RateParameterService,
  ReferenceService,
  TillCatalogueService,
  BankTransferService,
  CancellationPolicyService,
  DeskOfferService,
  TurnawayService,
  ConfirmationMailService,
  GuestService,
  SeasonPlanningService,
  ReservationService,
  ScopeSource,
  StayFeed,
  VegaMirror,
  VegaSeed,
  PmsProof,
  FiscalVersion,
  FiscalAudit,
  LedgerGate,
  CardFindings,
  CardTaps,
  BoardFigureSource,
  ReceptionPaymentsService,
  ArrivalBriefing,
  DepartureBriefing,
  TakingsBriefing,
  InvoicingService,
  PaymentService,
} from '@lemontaigu/pms-core';
import type { ActorSource } from '@lemontaigu/platform';
import { SeasonPlanningServiceImpl } from './seasonPlanning.js';
import {
  SupabaseRateProfileRepository,
  SupabaseSeasonCalendarRepository,
} from './supabase/seasonRepositories.js';
import {
  SupabaseAvailabilityQuery,
  SupabaseGuestRepository,
  SupabaseRateRepository,
  SupabasePropertyRepository,
  SupabaseReservationRepository,
  SupabaseRoomRepository,
} from './supabase/repositories.js';
import {
  AvailabilityServiceImpl,
  RateGridServiceImpl,
  RateParameterServiceImpl,
  ReferenceServiceImpl,
  TillCatalogueServiceImpl,
  BankTransferServiceImpl,
  CancellationPolicyServiceImpl,
  GuestServiceImpl,
  ReservationServiceImpl,
} from './services.js';
import { SupabaseTillCatalogueRepository } from './supabase/tillCatalogue.js';
import { SupabaseCancellationPolicyRepository } from './supabase/cancellationPolicy.js';
import { SupabaseBankTransferRepository } from './supabase/bankTransfer.js';
import { SupabaseDeskOfferRepository } from './supabase/deskOffer.js';
import { DeskOfferServiceImpl } from './deskOffer.js';
import { SupabaseTurnawayRepository } from './supabase/turnaway.js';
import { TurnawayServiceImpl } from './turnaway.js';
import {
  ConfirmationMailServiceImpl,
  HttpConfirmationMailDispatch,
  UnavailableConfirmationMailDispatch,
} from './confirmationMail.js';
import { SupabaseConfirmationMailRepository } from './supabase/confirmationMail.js';
import { systemClock } from './clock.js';
import { HttpChannelOpsService, UnavailableChannelOpsService, type ChannelOpsConfig } from './channelOps.js';
import { SupabaseVegaStayFeed, SupabaseBoardFigures } from './supabase/stayFeed.js';
import { SupabaseVegaMirror } from './supabase/vegaMirror.js';
import { SupabaseVegaSeed } from './supabase/vegaSeed.js';
import { SupabaseLedgerGate } from './supabase/ledgerGate.js';
import { SupabasePmsProof } from './supabase/proof.js';
import { SupabaseFiscalVersion } from './supabase/fiscalVersion.js';
import { SupabaseFiscalAudit } from './supabase/fiscalAudit.js';
import { SupabaseCardFindings } from './supabase/cardFindings.js';
import { SupabaseCardTaps } from './supabase/cardTaps.js';
import { SupabaseReceptionPayments } from './supabase/receptionPayments.js';
import { SupabaseVegaDepartureBriefing } from './supabase/departures.js';
import { SupabaseVegaArrivalBriefing } from './supabase/arrivals.js';
import { SupabaseVegaTakingsBriefing } from './supabase/takings.js';
import { SupabaseInvoicing, SupabasePayments } from './supabase/folioMoney.js';
import type { SupabaseClient } from './supabase/support.js';

/** The P1 PMS service surface exposed to drivers (bos.* today). */
export interface PmsServices {
  reservations: ReservationService;
  reference: ReferenceService;
  /** « Carte » — the till's catalogue, edited in the Restaurant group (till brief §16). */
  tillCatalogue: TillCatalogueService;
  /** « Conditions » — cancellation policies and their plans (PB7a). */
  cancellationPolicies: CancellationPolicyService;
  /** « Virement » — the property's bank-transfer instructions for arrhes (PB10). */
  bankTransfer: BankTransferService;
  /** The desk's quote and options (PB3): the website's direct price and lodging for a caller on the phone,
   *  under `pms.reservation.write`. */
  deskOffer: DeskOfferService;
  /** « Demandes sans suite » (PB9): calls that ended without a booking, counted anonymously. */
  turnaways: TurnawayService;
  /** The booking confirmation mail (M2–M3): prepare, card link, send, what is left to send. */
  confirmationMail: ConfirmationMailService;
  /** The cardex — search and the fiche client (PMS-CARDEX-BRIEF §6.1). */
  guests: GuestService;
  /** The rate grid — « Tarifs ». Separate from `reference` because the grid is
   *  the operational price position, not reference data, and it is the one
   *  surface where a mistake reaches an OTA. */
  rates: RateGridService;
  /** The parameter sets the grid is DERIVED from — « Tarifs ». Separate from
   *  `rates` because a set is an input record and the grid is its projection;
   *  one port holding both would invite the method that erases the line.
   *  ⚠ SUPERSEDED by `seasons` — retired with `pms_rate_parameter_set` at the
   *  brief's step 3, once nothing reads it. */
  rateParameters: RateParameterService;
  /** The season calendar × rate profile, and generating a grid from them
   *  (0118/0119). The two objects the welded parameter set split into. */
  seasons: SeasonPlanningService;
  availability: AvailabilityService;
  /** The établissements this actor may act in — the switcher's source, and the
   *  thing the composition root cannot know before it is read. */
  properties: PropertyRepository;
  /** The operator's channel controls (P2). Backed by the sync Function, because
   *  the Channex key is not in this runtime and must not be — see
   *  `channelOps.ts`. An app that supplies no `channel` config still gets a
   *  service here; it refuses with a sentence rather than being absent. */
  channel: ChannelOpsService;
  /** The daily stay actuals behind a source-neutral seam (METRICS-FEED-BRIEF
   *  §2). Vega today, this platform's own PMS from March 2027 — a driver that
   *  reads it never learns which, which is the entire point of the port.
   *
   *  Unscoped by construction: it takes a property code per call rather than
   *  binding one. The comparison surface asks about a NAMED établissement (the
   *  feed only exists for the hotel), and RLS still gates every read, so
   *  binding a scope here would add a second, weaker copy of a gate that
   *  already works. */
  stayFeed: StayFeed;
  /** The Vega mirror, read-only (VM1). Deliberately Vega-shaped — it is
   *  evidence, not a domain source, and §4 of the brief argues that
   *  abstracting it would destroy its only purpose. Gated by RLS on
   *  `pms.mirror.read`, which is operator-only by construction: `admin` holds
   *  a wildcard grant and no row was ever written for `staff`. */
  vegaMirror: VegaMirror;
  /** « Relecture Vega » (VEGA-SEED-BRIEF §12.1): the seed's run history, and the operator's manual run.
   *  Separate from `vegaMirror` on purpose — that port is read-only by construction, and asking for a
   *  relecture WRITES `pms_*` (never Vega). Gated on `pms.seed.manage`, operator-only like the mirror. */
  vegaSeed: VegaSeed;
  /** The PMS proof against Vega (PMS-PROOF-BRIEF §10): the closed days, their
   *  écarts and their classing. Operator-only (`pms.proof.read` / `.classify`). */
  proof: PmsProof;
  /** The fiscal software version (référentiel E21), shown in every tab's footer. Signed-in users only. */
  fiscalVersion: FiscalVersion;
  /** « Administration fiscale » (P3 F4c-2): gated on `pms.fiscal.audit` in every RPC. The archive download needs
   *  the Functions' origin (the `channel` config); without it, downloading answers a sentence, not a failure. */
  fiscalAudit: FiscalAudit;
  /** C6 Finance's freshness gate (PMS-LEDGER-L0-BRIEF §3). Gated on
   *  `fin.feed.gate`, which `staff` and `employee` hold as well as the operator:
   *  the banner reads the same to everyone. */
  ledgerGate: LedgerGate;
  /** Window-1 card findings (PMS-LEDGER-L0B-BRIEF). Gated on `fin.findings.read`
   *  (staff + operator); class E additionally on `fin.feed.read` (operator). */
  cardFindings: CardFindings;
  /** The Sextant taps of a date range, each with its state (TAP-VIEW brief T1). Gated on `fin.findings.read`. */
  cardTaps: CardTaps;
  /** What the desk typed into the manual board, for M2's side-by-side only.
   *
   *  An ACL over another context's table (R8), read-only and one-directional.
   *  ⚠ TEMPORARY — it exists to answer "do the two agree?" during the dual run.
   *  At M4 the rollup FEEDS the board, there is no second opinion left, and
   *  this goes out with the comparison screen. */
  boardFigures: BoardFigureSource;
  /** The desk's payment log — « Encaissement » (P3-T). Gated on
   *  `pms.payment.read`/`.write`, both `staff`-granted (0186), unlike
   *  `vegaMirror`'s operator-only capability. */
  receptionPayments: ReceptionPaymentsService;
  /** « Départs de demain » (PMS-TERMINAL-LINK-BRIEF §8.8) — the Vega mirror
   *  until go-live, the PMS folios after; the sheet does not change. */
  departures: DepartureBriefing;
  /** « Arrivées » — the departures sheet's twin: not yet arrived / arrived,
   *  with what each booking has already paid (§8.8g). */
  arrivals: ArrivalBriefing;
  /** « Encaissements du jour » — the day's money by tender, on both sheets
   *  (§8.8o): Vega's movements that day plus the form's not yet in Vega. */
  takings: TakingsBriefing;
  /** Factures and avoirs on a PMS folio (P3.1) — the école caisse until live (OB6). */
  invoicing: InvoicingService;
  /** Money in and out of a PMS folio, and the checkout (P3.2, OB6). */
  payments: PaymentService;
}

/**
 * `actor` is the L2 gate's source (ADR-0013 §4). It is a PORT, not the actor
 * itself, because these services are built once per mount while the capability
 * set arrives asynchronously and can be refreshed — see `ActorSource`.
 */
/** Until the property list arrives — and for a single-property actor, forever —
 *  the DATABASE decides: RLS narrows every read to the actor's grants and the
 *  RPCs resolve their établissement from `actor_default_property()`, which
 *  refuses when ambiguous. This is the safe resting state, not a gap. */
export const ACTOR_SCOPE: QueryScope = { kind: 'actor' };

/**
 * `scope` is a PORT for the same reason `actor` is. The services are built once
 * per mount; the operator's choice of établissement resolves asynchronously and
 * then CHANGES when they use the switcher. A scope captured by value here would
 * pin every repository to whatever was known at mount — which makes the
 * switcher a control that silently does nothing.
 */
export function buildPmsServices(
  db: SupabaseClient,
  actor: ActorSource,
  scope: ScopeSource = { current: () => ACTOR_SCOPE },
  /** Where the PMS Functions live and how to reach the caller's session.
   *  OPTIONAL: omitted, the channel controls resolve to the null object, so a
   *  test harness or an app that mounts the PMS without wiring the sync builds
   *  a complete surface and is refused only if it presses the button. */
  channel?: ChannelOpsConfig,
): PmsServices {
  const reservationRepo = new SupabaseReservationRepository(db, scope);
  const guestRepo = new SupabaseGuestRepository(db, scope);
  const roomRepo = new SupabaseRoomRepository(db, scope);
  const rateRepo = new SupabaseRateRepository(db, scope);
  const availabilityQuery = new SupabaseAvailabilityQuery(db, scope);

  return {
    reservations: new ReservationServiceImpl(reservationRepo, actor),
    reference: new ReferenceServiceImpl(roomRepo, rateRepo, actor),
    tillCatalogue: new TillCatalogueServiceImpl(new SupabaseTillCatalogueRepository(db, scope), actor),
    cancellationPolicies: new CancellationPolicyServiceImpl(new SupabaseCancellationPolicyRepository(db, scope), actor),
    bankTransfer: new BankTransferServiceImpl(new SupabaseBankTransferRepository(db, scope), actor),
    deskOffer: new DeskOfferServiceImpl(new SupabaseDeskOfferRepository(db), actor, systemClock),
    turnaways: new TurnawayServiceImpl(new SupabaseTurnawayRepository(db), actor),
    confirmationMail: new ConfirmationMailServiceImpl(
      new SupabaseConfirmationMailRepository(db),
      channel ? new HttpConfirmationMailDispatch(channel) : new UnavailableConfirmationMailDispatch(),
      actor,
    ),
    guests: new GuestServiceImpl(guestRepo, actor),
    rates: new RateGridServiceImpl(rateRepo, actor),
    rateParameters: new RateParameterServiceImpl(rateRepo, actor),
    seasons: new SeasonPlanningServiceImpl(
      new SupabaseSeasonCalendarRepository(db, scope),
      new SupabaseRateProfileRepository(db, scope),
      rateRepo,
      roomRepo,
      actor,
    ),
    availability: new AvailabilityServiceImpl(availabilityQuery, actor),
    properties: new SupabasePropertyRepository(db),
    channel: channel
      ? new HttpChannelOpsService(channel, actor)
      : new UnavailableChannelOpsService(),
    stayFeed: new SupabaseVegaStayFeed(db),
    vegaMirror: new SupabaseVegaMirror(db),
    vegaSeed: new SupabaseVegaSeed(db),
    proof: new SupabasePmsProof(db),
    fiscalVersion: new SupabaseFiscalVersion(db),
    fiscalAudit: new SupabaseFiscalAudit(db, channel ? { apiBase: channel.apiBase, accessToken: channel.accessToken } : null),
    ledgerGate: new SupabaseLedgerGate(db),
    cardFindings: new SupabaseCardFindings(db),
    cardTaps: new SupabaseCardTaps(db),
    boardFigures: new SupabaseBoardFigures(db),
    receptionPayments: new SupabaseReceptionPayments(db, scope, actor),
    departures: new SupabaseVegaDepartureBriefing(db),
    arrivals: new SupabaseVegaArrivalBriefing(db),
    takings: new SupabaseVegaTakingsBriefing(db),
    invoicing: new SupabaseInvoicing(db, actor),
    payments: new SupabasePayments(db, actor),
  };
}
